// `defineCatalog`: aggregates every declaration into the single input of the router adapter, the client and codegen.
import type { AnyEventDef } from './event.ts';
import type { OperationDef } from './operations.ts';
import type { PermissionDef } from './permissions.ts';
import type { SettingDef } from './settings.ts';

type ByName<D extends readonly { readonly name: string }[]> = { readonly [K in D[number] as K['name']]: K };

/** A union of operations keyed by name: the catalog's written-out type (contracts `catalog.ts`). */
export type OperationsByName<U extends { readonly name: string }> = { readonly [K in U as K['name']]: K };

type Exactly<A, B> = [A] extends [B] ? ([B] extends [A] ? true : false) : false;

/**
 * Compile-time only: the catalog's runtime list and its `Operation` type name the same operations. On a mismatch the
 * argument's type is `false` and the call fails typecheck; hover it to read `Exclude<A, B>` (listed but not typed) and
 * `Exclude<B, A>` (typed but not listed).
 */
export function listsInStep<A, B>(same: Exactly<A, B> extends true ? true : { readonly listedNotTyped: Exclude<A, B>; readonly typedNotListed: Exclude<B, A> }): void {
  void same;
}

export interface Catalog<
  O extends Readonly<Record<string, OperationDef>> = Readonly<Record<string, OperationDef>>,
> {
  readonly operations: O;
  readonly events: Readonly<Record<string, AnyEventDef>>;
  readonly settings: Readonly<Record<string, SettingDef>>;
  readonly permissions: Readonly<Record<string, PermissionDef>>;
}

const byName = <D extends readonly { readonly name: string }[]>(defs: D): ByName<D> =>
  Object.fromEntries(defs.map((d) => [d.name, d])) as ByName<D>;

export function defineCatalog<const Ops extends readonly OperationDef[]>(parts: {
  readonly operations: Ops;
  readonly events: readonly AnyEventDef[];
  readonly settings: Readonly<Record<string, SettingDef>>;
  readonly permissions: Readonly<Record<string, PermissionDef>>;
}): Catalog<ByName<Ops>> {
  return { ...parts, operations: byName(parts.operations), events: byName(parts.events) };
}
